import { useCallback, useEffect, useMemo, useReducer } from 'react';
import ReactGA from 'react-ga4';
// context
// hooks
import { useJune } from '@hooks/use-june';
// utils
import { enqueueSnackbar } from 'notistack';
import { AuthContext } from './auth-context';
// types
import { useGrowintel } from '@growdash/growintel/hooks/useGrowIntel';
import {
  SignUpDto,
  SignUpDtoIndustryEnum,
  VerifyEmailDto
} from '@growdash/identity/generated';
import { identityClient } from '@lib/identity';
import { paths } from '@routes/paths';
import { isTokenExpired } from '@utils/jwt';
import { RoleType } from '@utils/roles';
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  setCookieObject,
  setSession,
  setSessionRefreshToken,
  USER_KEY
} from '@utils/session';
import { ActionMapType, AuthStateType, AuthUserType } from './types';

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  RESET_PASSWORD = 'RESET_PASSWORD',
  VERIFY_EMAIL = 'VERIFY_EMAIL'
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.RESET_PASSWORD]: undefined;
  [Types.VERIFY_EMAIL]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null
    };
  }
  if (action.type === Types.RESET_PASSWORD) {
    return {
      ...state,
      user: null
    };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const growIntel = useGrowintel();
  const analytics: ReturnType<typeof useJune> = useJune('q3ClmW5XJpuu6QHF');

  // REFRESH USER
  const refreshUser = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        console.error('No refresh token available for user refresh');
        return false;
      }

      // First, call the refresh token endpoint to get new tokens
      const refreshResponse =
        await identityClient.authAPI.authControllerRefreshToken({
          headers: {
            Authorization: `Bearer ${refreshToken}`
          }
        });

      const newAccessToken = refreshResponse.data.access_token;
      const newRefreshToken = refreshResponse.data.refresh_token;

      if (!newAccessToken) {
        console.error('No new access token received from refresh endpoint');
        return false;
      }

      // Update session with new tokens
      setSession(newAccessToken as any, newRefreshToken);

      // Now get user info with the new access token
      const { data: user } =
        await identityClient.usersAPI.usersControllerGetUserInfo({
          headers: {
            Authorization: `Bearer ${newAccessToken}`
          }
        });

      if (user) {
        setCookieObject({ key: USER_KEY, value: user });
        dispatch({
          type: Types.LOGIN,
          payload: {
            user
          }
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return false;
    }
  }, []);

  const initialize = useCallback(async () => {
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.startsWith(paths.auth.jwt.root);

    const isLoggingOut = sessionStorage.getItem('isLoggingOut');
    if (isLoggingOut) {
      return;
    }

    try {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      // Clear session and redirect if no token or expired token
      if (!accessToken) {
        clearSession();
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null
          }
        });
        if (!isAuthPage) {
          window.location.href = paths.auth.jwt.login;
        }
        return;
      }
      if (isTokenExpired(accessToken)) {
        if (!refreshToken) {
          clearSession();
          dispatch({ type: Types.INITIAL, payload: { user: null } });
          if (!isAuthPage) {
            window.location.href = paths.auth.jwt.login;
          }
          return;
        }
      }

      try {
        // Verify token by getting user info
        const { data: user } =
          await identityClient.usersAPI.usersControllerGetUserInfo({
            headers: {
              Authorization: `Bearer ${
                isTokenExpired(accessToken) ? refreshToken : accessToken
              }`
            }
          });
        if (user) {
          // Valid user, set session data
          setCookieObject({ key: USER_KEY, value: user });

          // Create a wrapper to ensure refreshUser is properly bound
          const refreshUserWrapper = async () => {
            if (typeof refreshUser === 'function') {
              return await refreshUser();
            }
            console.error('refreshUser is not a function:', refreshUser);
            return false;
          };

          growIntel.config.initializeGrowintel({
            accessToken: accessToken as unknown as string,
            service_urls: {
              growintel: import.meta.env.VITE_REACT_APP_GROWINTEL_API_URL,
              identity: import.meta.env.VITE_REACT_APP_IDENTITY_API_URL,
              aisha: import.meta.env.VITE_REACT_APP_AISHA_GOALS_API_URL
            },
            refreshUser: refreshUserWrapper
          });
          setSessionRefreshToken(refreshToken as string);
          setSession(accessToken as string, refreshToken as string);
          dispatch({
            type: Types.INITIAL,
            payload: {
              user
            }
          });
        } else {
          // No user data returned, clear session and redirect
          clearSession();
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null
            }
          });
          if (!isAuthPage) {
            window.location.href = paths.auth.jwt.login;
          }
        }
      } catch (error) {
        // API call failed, clear session and redirect
        clearSession();
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null
          }
        });
        if (!isAuthPage) {
          window.location.href = paths.auth.jwt.login;
        }
        enqueueSnackbar('Session expired or invalid. Please log in again.', {
          variant: 'error'
        });
      }
    } catch (error) {
      // Unexpected error, clear session and redirect
      clearSession();
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null
        }
      });
      if (!isAuthPage) {
        window.location.href = paths.auth.jwt.login;
      }
      enqueueSnackbar('An unexpected error occurred. Please log in again.', {
        variant: 'error'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshUser]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (username: string, password: string) => {
      const data = {
        username,
        password
      };

      const loginResponse =
        await identityClient.authAPI.authControllerSignIn(data);
      const accessToken = loginResponse.data.access_token;
      const refreshToken = loginResponse.data.refresh_token;
      await setSession(accessToken as any, refreshToken as string);
      const { data: user } =
        await identityClient.usersAPI.usersControllerGetUserInfo({
          headers: { Authorization: `Bearer ${accessToken}` }
        });
      setCookieObject({ key: USER_KEY, value: user });

      // Initialize GrowinTel after successful login
      const refreshUserWrapper = async () => {
        if (typeof refreshUser === 'function') {
          return await refreshUser();
        }
        console.error('refreshUser is not a function in login:', refreshUser);
        return false;
      };

      growIntel.config.initializeGrowintel({
        accessToken: accessToken as unknown as string,
        service_urls: {
          growintel: import.meta.env.VITE_REACT_APP_GROWINTEL_API_URL,
          identity: import.meta.env.VITE_REACT_APP_IDENTITY_API_URL,
          aisha: import.meta.env.VITE_REACT_APP_AISHA_GOALS_API_URL
        },
        refreshUser: refreshUserWrapper
      });

      const gaUserId = username;
      ReactGA.set({ userId: gaUserId });

      if (analytics) {
        analytics.identify(user.username.toLowerCase(), {
          email: user.username,
          name: user.name,
          country: user.country.name,
          client: !user?.roles?.includes(RoleType.MANAGER)
            ? user.client.name
            : '-',
          roles: user.roles
        });
        analytics.track(
          'Signed In',
          {
            browser: window.navigator.userAgent
          },
          {
            // Add the GROUP_ID here to track this event on behalf of a workspace
            context: {
              groupId: !user?.roles?.includes(RoleType.MANAGER)
                ? user.client.name
                : 'Customer Service'
            }
          }
        );
      }

      dispatch({
        type: Types.LOGIN,
        payload: {
          user
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analytics]
  );

  // RESET PASSWORD
  const resetPassword = useCallback(async (password: string, token: string) => {
    await identityClient.authAPI.authControllerResetPassword(token, {
      password
    });

    dispatch({
      type: Types.RESET_PASSWORD
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    try {
      sessionStorage.setItem('isLoggingOut', 'true');

      clearSession();

      ReactGA.set({ userId: null });

      dispatch({
        type: Types.LOGOUT
      });

      sessionStorage.removeItem('isLoggingOut');

      setTimeout(() => {
        window.location.href = paths.auth.jwt.login;
      }, 100);
    } catch (error) {
      console.error('Error during logout:', error);
      sessionStorage.removeItem('isLoggingOut');
      clearSession();
      window.location.href = paths.auth.jwt.login;
    }
  }, []);

  // REGISTER
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      countryId: number,
      industry: string = SignUpDtoIndustryEnum.Others,
      companyName: string = '',
      companyLegalName: string = '',
      otp: string = ''
    ) => {
      try {
        const signUpDto: SignUpDto = {
          email,
          password,
          firstName,
          lastName,
          countryId,
          industry: industry as SignUpDtoIndustryEnum,
          companyName: companyName || undefined,
          companyLegalName: companyLegalName || undefined,
          otp
        };

        // Call the signup endpoint
        await identityClient.authAPI.authControllerSignUp(signUpDto);

        // After successful signup, automatically sign in the user
        try {
          const signInResponse =
            await identityClient.authAPI.authControllerSignIn({
              username: email,
              password
            });

          if (signInResponse.data.access_token) {
            const accessToken = signInResponse.data.access_token;
            const refreshToken = signInResponse.data.refresh_token;

            await setSession(accessToken as any, refreshToken as string);

            const { data: user } =
              await identityClient.usersAPI.usersControllerGetUserInfo({
                headers: { Authorization: `Bearer ${accessToken}` }
              });

            setCookieObject({ key: USER_KEY, value: user });

            const refreshUserWrapper = async () => {
              if (typeof refreshUser === 'function') {
                return await refreshUser();
              }
              console.error(
                'refreshUser is not a function in register:',
                refreshUser
              );
              return false;
            };

            growIntel.config.initializeGrowintel({
              accessToken: accessToken as unknown as string,
              service_urls: {
                growintel: import.meta.env.VITE_REACT_APP_GROWINTEL_API_URL,
                identity: import.meta.env.VITE_REACT_APP_IDENTITY_API_URL,
                aisha: import.meta.env.VITE_REACT_APP_AISHA_GOALS_API_URL
              },
              refreshUser: refreshUserWrapper
            });

            // Set analytics
            ReactGA.set({ userId: email });

            if (analytics) {
              analytics.identify(user.username.toLowerCase(), {
                email: user.username,
                name: user.name,
                country: user.country.name,
                client: !user?.roles?.includes(RoleType.MANAGER)
                  ? user.client.name
                  : '-',
                roles: user.roles
              });
              analytics.track(
                'Signed Up',
                {
                  browser: window.navigator.userAgent
                },
                {
                  context: {
                    groupId: !user?.roles?.includes(RoleType.MANAGER)
                      ? user.client.name
                      : 'Customer Service'
                  }
                }
              );
            }

            dispatch({
              type: Types.LOGIN,
              payload: {
                user
              }
            });

            // Track signup event
            ReactGA.event({
              category: 'User',
              action: 'Signup',
              label: 'Success'
            });

            return {
              success: true,
              data: {
                authenticated: true,
                user
              }
            };
          }
        } catch (error) {
          console.error('Auto sign-in after registration failed:', error);
        }

        return {
          success: true,
          data: {}
        };
      } catch (error) {
        console.error('Registration error:', error);
        return {
          success: false,
          error
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshUser, analytics]
  );

  // VERIFY EMAIL
  const verifyEmail = useCallback(async (email: string) => {
    try {
      const verifyEmailDto: VerifyEmailDto = {
        email
      };

      const response =
        await identityClient.authAPI.authControllerVerifyEmail(verifyEmailDto);

      // If verification is successful, automatically log in the user
      if (response.data.access_token) {
        const accessToken = response.data.access_token;
        const refreshToken = response.data.refresh_token;
        setSession(accessToken as any, refreshToken);

        const { data: user } =
          await identityClient.usersAPI.usersControllerGetUserInfo({
            headers: { Authorization: `Bearer ${accessToken}` }
          });

        setCookieObject({ key: USER_KEY, value: user });

        dispatch({
          type: Types.LOGIN,
          payload: {
            user
          }
        });

        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Email verification error:', error);
      return {
        success: false,
        error
      };
    }
  }, []);

  const token = getAccessToken();
  const authStatus =
    token && !isTokenExpired(token as string)
      ? 'authenticated'
      : 'unauthenticated';
  const status = state.loading ? 'loading' : authStatus;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      login,
      logout,
      resetPassword,
      register,
      verifyEmail,
      refreshUser
    }),
    [
      login,
      logout,
      register,
      resetPassword,
      state.user,
      status,
      verifyEmail,
      refreshUser
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
